import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

export type ListingPromotionOption = {
  id: string;
  title: string;
  description: string;
  price: number;
  currency?: string;
  badge?: string;
  recommended?: boolean;
  icon?: React.ReactNode;
};

export type ListingPromotionSelectorProps = {
  options: ListingPromotionOption[];
  value: string[];
  onChange: (next: string[]) => void;
  title?: string;
  subtitle?: string;
};

const ListingPromotionSelector: React.FC<ListingPromotionSelectorProps> = ({
  options,
  value,
  onChange,
  title = 'Boost your listing',
  subtitle = 'Choose add-ons to increase visibility and get more applicants.',
}) => {
  const theme = useTheme();

  const selected = React.useMemo(() => new Set(value || []), [value]);

  const total = React.useMemo(() => {
    const byId = new Map(options.map((o) => [o.id, o] as const));
    let sum = 0;
    for (const id of Array.from(selected)) {
      const opt = byId.get(id);
      if (opt) sum += Number(opt.price) || 0;
    }
    return sum;
  }, [options, selected]);

  const currency = React.useMemo(() => {
    const first = options.find((o) => o.currency)?.currency;
    return first || 'EUR';
  }, [options]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6" fontWeight={900}>
                {title}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
            }}
          >
            {options.map((opt) => {
              const isSelected = selected.has(opt.id);
              const accent = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

              return (
                <Card
                  key={opt.id}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: isSelected ? 'transparent' : 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: isSelected ? 6 : 0,
                    ...(isSelected && {
                      backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), ${accent}`,
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'padding-box, border-box',
                    }),
                  }}
                >
                  <CardActionArea onClick={() => toggle(opt.id)} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2.25 }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                              {opt.icon || <AutoAwesomeIcon fontSize="small" />}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={900} noWrap>
                                {opt.title}
                              </Typography>
                              {opt.badge && (
                                <Chip
                                  size="small"
                                  label={opt.badge}
                                  color={opt.recommended ? 'primary' : 'default'}
                                  variant={opt.recommended ? 'filled' : 'outlined'}
                                  sx={{ mt: 0.5, fontWeight: 800 }}
                                />
                              )}
                            </Box>
                          </Stack>

                          <Stack alignItems="flex-end" spacing={0.5}>
                            <Typography fontWeight={900}>
                              {opt.price.toFixed(2)} {opt.currency || currency}
                            </Typography>
                            {isSelected && (
                              <Chip
                                icon={<CheckCircleIcon />}
                                size="small"
                                label="Selected"
                                color="success"
                                sx={{ fontWeight: 800 }}
                              />
                            )}
                          </Stack>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {opt.description}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>

          <Divider />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Selected: <strong>{value?.length || 0}</strong>
            </Typography>
            <Typography variant="h6" fontWeight={900}>
              Total: {total.toFixed(2)} {currency}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ListingPromotionSelector;
